/**
 * Local Authorization List store (SPEC-001).
 *
 * Reads the Dexie-backed list for the current station + exposes the
 * mutations the Settings UI needs (manual add / edit / delete + the
 * "full replace" import path). Inbound SendLocalList from the CSMS
 * is handled outside this store (in `useStationStore.connect`'s
 * `handleCsmsCall`), so domain operations stay in `@/domain/LocalAuthList`.
 */
import { create } from 'zustand';
import { db } from '@/persistence/dexie/db';
import {
  LocalAuthListRepositoryDexie,
  LocalAuthListVersionRepositoryDexie,
} from '@/persistence/dexie/repositories';
import type { AuthStatus, StationId } from '@/domain/types';
import type { LocalAuthListEntrySnapshot } from '@/domain/LocalAuthList';

const repo = new LocalAuthListRepositoryDexie(db);
const versionRepo = new LocalAuthListVersionRepositoryDexie(db);

export interface LocalAuthListStoreState {
  readonly entries: readonly LocalAuthListEntrySnapshot[];
  readonly listVersion: number;
  readonly status: 'idle' | 'loading' | 'ready';
}

export interface NewEntryInput {
  readonly idTag: string;
  readonly status: AuthStatus;
  readonly expiryDate?: string;
  readonly parentIdTag?: string;
}

export interface LocalAuthListStoreActions {
  refresh(stationId: StationId): Promise<void>;
  add(stationId: StationId, entry: NewEntryInput): Promise<boolean>;
  /** Delete a row by idTag. Bumps the version so the CSMS sees the diff. */
  remove(stationId: StationId, idTag: string): Promise<void>;
  /** Replace the whole list (e.g. from JSON import). */
  replaceAll(stationId: StationId, entries: readonly NewEntryInput[]): Promise<void>;
  /** Manual version-bump so the user can mark the list as "changed". */
  setVersion(stationId: StationId, listVersion: number): Promise<void>;
}

export type LocalAuthListStore = LocalAuthListStoreState & LocalAuthListStoreActions;

function rowToSnap(row: {
  idTag: string;
  status: AuthStatus;
  expiryDate?: string;
  parentIdTag?: string;
}): LocalAuthListEntrySnapshot {
  return {
    idTag: row.idTag,
    status: row.status,
    ...(row.expiryDate !== undefined ? { expiryDate: row.expiryDate } : {}),
    ...(row.parentIdTag !== undefined ? { parentIdTag: row.parentIdTag } : {}),
  };
}

async function reload(stationId: StationId): Promise<{
  entries: LocalAuthListEntrySnapshot[];
  listVersion: number;
}> {
  const [rows, version] = await Promise.all([
    repo.byStation(stationId),
    versionRepo.get(stationId),
  ]);
  return {
    entries: rows.map(rowToSnap),
    listVersion: version?.listVersion ?? 0,
  };
}

export const useLocalAuthListStore = create<LocalAuthListStore>((set) => ({
  entries: [],
  listVersion: 0,
  status: 'idle',
  async refresh(stationId): Promise<void> {
    set({ status: 'loading' });
    const next = await reload(stationId);
    set({ ...next, status: 'ready' });
  },
  async add(stationId, entry): Promise<boolean> {
    if (entry.idTag.length === 0 || entry.idTag.length > 20) return false;
    await repo.upsert({
      stationId,
      idTag: entry.idTag,
      status: entry.status,
      ...(entry.expiryDate !== undefined && entry.expiryDate.length > 0
        ? { expiryDate: entry.expiryDate }
        : {}),
      ...(entry.parentIdTag !== undefined && entry.parentIdTag.length > 0
        ? { parentIdTag: entry.parentIdTag }
        : {}),
      updatedAt: Date.now(),
    });
    // Bump the version so a watching CSMS sees the diff.
    const current = await versionRepo.get(stationId);
    await versionRepo.set(stationId, (current?.listVersion ?? 0) + 1);
    set(await reload(stationId));
    return true;
  },
  async remove(stationId, idTag): Promise<void> {
    await repo.delete(stationId, idTag);
    const current = await versionRepo.get(stationId);
    await versionRepo.set(stationId, (current?.listVersion ?? 0) + 1);
    set(await reload(stationId));
  },
  async replaceAll(stationId, entries): Promise<void> {
    const now = Date.now();
    await repo.replaceAll(
      stationId,
      entries.map((e) => ({
        stationId,
        idTag: e.idTag,
        status: e.status,
        ...(e.expiryDate !== undefined ? { expiryDate: e.expiryDate } : {}),
        ...(e.parentIdTag !== undefined ? { parentIdTag: e.parentIdTag } : {}),
        updatedAt: now,
      })),
    );
    const current = await versionRepo.get(stationId);
    await versionRepo.set(stationId, (current?.listVersion ?? 0) + 1);
    set(await reload(stationId));
  },
  async setVersion(stationId, listVersion): Promise<void> {
    await versionRepo.set(stationId, listVersion);
    set(await reload(stationId));
  },
}));
